/* eslint-disable max-len */
import { toastr } from 'react-redux-toastr';
import {
    pendingTask, // The action key for modifying loading state
    begin, // The action value if a "long" running task begun
    end, // The action value if a "long" running task ended
} from 'react-redux-spinner';
import * as api from '../../services/api-endpoints';
import i18n from '../../../services/i18n';
import { UPDATE_SETUP, RESET_SETUP } from './setup.actions';
import { ADD_SETUP_TO_LIST, UPDATE_ADMIN_APP_DATA } from '../appData';
import { LOADING_END, LOADING_START } from '../../../store/loading';
import { getScheduleSourceLabel, getAutoApprovalLabel, getBidProcessTypeLabel } from '../../../Enums/Helpers/LabellingHelper';
import { NotificationType } from '../../../Enums';
import { noneTemplateId, defaultTimeValue, defaultTimeUnit } from './setup.reducer';
/**
 * @param {ApiAdmin.NotificationTemplatePresentationModel[]} notifications
 */
const parseReceivedNotifications = (notifications) => Object.values(NotificationType).map((notifType) => {
    const notificationSet = notifications.find((x) => x.type === notifType);
    return {
        type: notifType,
        templateId: notificationSet?.templateId || noneTemplateId,
        timeValue: notificationSet?.timeValue || defaultTimeValue,
        timeUnit: notificationSet?.timeUnit || defaultTimeUnit,
    };
});

/**
 * @param {string} adminSetupId
 * @param {AdminSidePanelAction} adminSidePanelAction
 * @param {(() => void)} callBack
 */
export const loadSetup = (adminSetupId, adminSidePanelAction, callBack) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.getSetup(adminSetupId);
        if (response.status === 200) {
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    ...response.data,
                    adminSidePanelAction,
                    notificationTemplates: parseReceivedNotifications(response.data.notificationTemplates),
                },
            });
            callBack();
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.loadingBidSetup'), i18n.t('shiftBidding:toastr.loadingFailed'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingBidSetup'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} adminSetupId
 * @param {AdminSidePanelAction} adminSidePanelAction
 * @param {(() => void)} callBack
 */
export const loadDuplicateSetup = (adminSetupId, adminSidePanelAction, callBack) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.getSetup(adminSetupId);
        if (response.status === 200) {
            dispatch({
                type: RESET_SETUP,
            });
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    ...response.data,
                    adminSidePanelAction,
                    name: `${i18n.t('shiftBidding:admin.common.copyOf')} ${response.data.name}`,
                    notificationTemplates: parseReceivedNotifications(response.data.notificationTemplates),
                },
            });
            callBack();
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.loadingDuplicateBidSetup'), i18n.t('shiftBidding:toastr.loadingDuplicateFailed'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.loadingDuplicateBidSetup'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {Setup.ISetupDetails} adminSetup
 * @param {{
 *  hyperfinds: ManagerAppData.IHyperfind[],
 * }} storeData
 * @param {(() => void)} callBack
 * @param {boolean} isDuplicating
 */
export const createSetup = (adminSetup, storeData, callBack, isDuplicating) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { hyperfinds } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        /** @type {ApiAdmin.ISetupDetailsPresentationModel} */
        const parsedAdminSetup = {
            ...adminSetup,
            // @ts-ignore
            notificationTemplates: adminSetup.notificationTemplates.map((x) => ({ ...x, templateId: x.templateId !== noneTemplateId ? x.templateId : null })),
        };
        const response = await api.createSetup(parsedAdminSetup);
        if (response.status === 200) {
            dispatch({
                type: ADD_SETUP_TO_LIST,
                payload: {
                    ...response.data,
                    bidProcessTypeName: getBidProcessTypeLabel(response.data.bidProcessType),
                    autoApprovalName: getAutoApprovalLabel(response.data.isAutoApproval),
                    scheduleSourceName: getScheduleSourceLabel(response.data.scheduleSource),
                    creationHyperfindName: hyperfinds.find((x) => x.value === response.data.creationHyperfindId)?.label || '',
                    managementHyperfindName: hyperfinds.find((x) => x.value === response.data.managementHyperfindId)?.label || '',
                    viewHyperfindName: hyperfinds.find((x) => x.value === response.data.viewHyperfindId)?.label || '',
                },
            });
            toastr.success(`${isDuplicating ? i18n.t('shiftBidding:admin.common.action.duplicating') : i18n.t('shiftBidding:admin.common.action.creating')} ${i18n.t('shiftBidding:admin.common.configSet')}`, `${i18n.t('shiftBidding:admin.common.configSet')} ${isDuplicating ? i18n.t('shiftBidding:admin.common.action.duplicated') : i18n.t('shiftBidding:admin.common.action.created')}`);
            dispatch({ type: RESET_SETUP });
            callBack();
        } else {
            toastr.error(`${isDuplicating ? i18n.t('shiftBidding:admin.common.action.duplicating') : i18n.t('shiftBidding:admin.common.action.creating')} ${i18n.t('shiftBidding:admin.common.configSet')}`, `${isDuplicating ? i18n.t('shiftBidding:admin.common.action.duplication') : i18n.t('shiftBidding:admin.common.action.creation')} ${i18n.t('shiftBidding:admin.common.action.failed')}`);
        }
    } catch (error) {
        toastr.error(`${isDuplicating ? i18n.t('shiftBidding:admin.common.action.duplicating') : i18n.t('shiftBidding:admin.common.action.creating')} ${i18n.t('shiftBidding:admin.common.configSet')}`, error.response?.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {Setup.ISetupDetails} adminSetup
 * @param {{
 *  setups: Setup.ISetupBrief[],
 *  hyperfinds: ManagerAppData.IHyperfind[],
 * }} storeData
 * @param {(() => void)} callBack
 */
export const editSetup = (adminSetup, storeData, callBack) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { setups, hyperfinds } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        /** @type {ApiAdmin.ISetupDetailsPresentationModel} */
        const parsedAdminSetup = { // Only the following fields are editable as per https://miro.com/app/board/o9J_klClZ9E=/?moveToWidget=3458764532884761669&cot=14
            ...adminSetup,
            // @ts-ignore
            notificationTemplates: adminSetup.notificationTemplates.map((x) => ({ ...x, templateId: x.templateId !== noneTemplateId ? x.templateId : null })),
        };
        const response = await api.updateSetup(adminSetup.bidSetupId, parsedAdminSetup);
        if (response.status === 200) {
            const updatedSetup = response.data;
            const indexToUpdate = setups.findIndex((x) => x.bidSetupId === updatedSetup.bidSetupId);
            
            setups[indexToUpdate] = {
                ...updatedSetup,
                bidProcessTypeName: getBidProcessTypeLabel(updatedSetup.bidProcessType),
                autoApprovalName: getAutoApprovalLabel(updatedSetup.isAutoApproval),
                scheduleSourceName: getScheduleSourceLabel(updatedSetup.scheduleSource),
                creationHyperfindName: hyperfinds.find((x) => x.value === updatedSetup.creationHyperfindId)?.label || '',
                managementHyperfindName: hyperfinds.find((x) => x.value === updatedSetup.managementHyperfindId)?.label || '',
                viewHyperfindName: hyperfinds.find((x) => x.value === updatedSetup.viewHyperfindId)?.label || ''
            };

            dispatch({
                type: UPDATE_ADMIN_APP_DATA,
                payload: { setups },
            });
            dispatch({ type: RESET_SETUP });
            callBack();
        } else {
            toastr.error(`${i18n.t('shiftBidding:admin.common.action.saving')} ${i18n.t('shiftBidding:admin.common.configSet')}`, `${i18n.t('shiftBidding:admin.common.action.saving')} ${i18n.t('shiftBidding:admin.common.action.failed')}`);
        }
    } catch (error) {
        toastr.error(`${i18n.t('shiftBidding:admin.common.action.saving')} ${i18n.t('shiftBidding:admin.common.configSet')}`, error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} adminSetupId
 * @param {{setups: Setup.ISetupBrief[]}} storeData
 */
export const deleteSetup = (adminSetupId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { setups } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.deleteSetup(adminSetupId);
        if (response.status === 200) {
            const indexToRemove = setups.findIndex((x) => x.bidSetupId === adminSetupId);
            setups.splice(indexToRemove, 1);
            dispatch({
                type: UPDATE_ADMIN_APP_DATA,
                payload: { setups },
            });
            toastr.success(`${i18n.t('shiftBidding:admin.common.action.deleting')} ${i18n.t('shiftBidding:admin.common.configSet')}`, `${i18n.t('shiftBidding:admin.common.configSet')} ${i18n.t('shiftBidding:admin.common.action.successfully')} ${i18n.t('shiftBidding:admin.common.action.deleted')}`);
        } else {
            toastr.error(`${i18n.t('shiftBidding:admin.common.action.deleting')} ${i18n.t('shiftBidding:admin.common.configSet')}`, `${i18n.t('shiftBidding:admin.common.action.deleting')} ${i18n.t('shiftBidding:admin.common.action.failed')}`);
        }
    } catch (error) {
        toastr.error(`${i18n.t('shiftBidding:admin.common.action.deleting')} ${i18n.t('shiftBidding:admin.common.configSet')}`, error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
